import { useEffect } from "react";

/**
 * Global Cmd/Ctrl+Enter handler that "commits" the topmost open
 * dialog by clicking whichever button carries `data-modal-primary`.
 *
 * Companion to `EscapeToClose` — together they cover full-keyboard
 * modal triage: Esc to bail out, Cmd/Ctrl+Enter to commit. Modals
 * opt in by tagging their primary action button (Save, Yes-delete,
 * Acknowledge, Confirm, etc.) with `data-modal-primary="true"`.
 * Skipped when the button is disabled so a rapid keystroke can't
 * fire a not-yet-ready action.
 *
 * IMPORTANT: does NOT shadow the existing `useSaveHotkey` (Ctrl+S)
 * that page-level "Save" buttons use — that hook binds Ctrl/⌘+S,
 * this one binds Ctrl/⌘+Enter. Different keys, no conflict.
 *
 * Skipped if the user is inside a `<textarea>` (Enter has native
 * newline semantics there) OR inside a native `<input type="text">`
 * without any dialog open (rare — we'd never fire outside a dialog
 * anyway since the query is scoped to dialogs).
 */
export default function ModalPrimaryHotkey() {
  useEffect(() => {
    const handler = (e) => {
      const isModCombo =
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        !e.altKey &&
        (e.key === "Enter" || e.key === "\n");
      if (!isModCombo) return;
      // Textarea preserves Enter semantics — leave it alone.
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === "textarea") return;
      const dialogs = document.querySelectorAll('[role="dialog"]');
      if (dialogs.length === 0) return;
      const top = dialogs[dialogs.length - 1];
      const primary = top.querySelector('[data-modal-primary="true"]');
      if (!primary) return;
      // Respect the disabled state — the modal's own dirty-check /
      // busy guard is authoritative. Also blocks accidental
      // double-commits while an in-flight save is still pending.
      if (primary.disabled || primary.getAttribute("aria-disabled") === "true") {
        return;
      }
      e.preventDefault();
      primary.click();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  return null;
}
